import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Layout } from '../components/layout/Layout'
import { useAuth } from '../auth/AuthContext'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

export interface Task {
  id: string
  title: string
  description: string
  assigneeUid: string | null
  assigneeEmail: string | null
  assigneeName: string | null
  createdBy: string
  createdByName: string | null
  dueDate: string | null
  status: 'open' | 'done'
  sourceDocumentId: string | null
  sourceDocumentTitle: string | null
  completedAt: string | null
  createdAt: string | null
  updatedAt: string | null
}

interface Member {
  uid: string
  email: string
  displayName: string
}

// メンバーごとのアバター色（Googleカレンダー風）
const AVATAR_COLORS = ['#2563eb', '#059669', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#be185d', '#4d7c0f']
export function memberColor(uid: string): string {
  let hash = 0
  for (let i = 0; i < uid.length; i++) hash = (hash * 31 + uid.charCodeAt(i)) | 0
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

function memberLabel(m: Member | undefined | null): string {
  if (!m) return '不明'
  return m.displayName || m.email || m.uid
}

function MemberAvatar({ member, size = 22 }: { member: Member | undefined | null; size?: number }) {
  const label = memberLabel(member)
  return (
    <span
      title={label}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: member ? memberColor(member.uid) : 'var(--color-border, #d1d5db)',
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.5,
        fontWeight: 600,
        flexShrink: 0
      }}
    >
      {label.charAt(0).toUpperCase()}
    </span>
  )
}

function isOverdue(dueDate: string | null, status: string): boolean {
  if (!dueDate || status === 'done') return false
  const today = new Date()
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
  return dueDate < todayStr
}

function formatDue(dueDate: string): string {
  const [y, m, d] = dueDate.split('-').map(Number)
  const now = new Date()
  return y === now.getFullYear() ? `${m}/${d}` : `${y}/${m}/${d}`
}

/** @メンション自動補完付きテキストエリア */
function MentionTextarea({
  value,
  onChange,
  members,
  placeholder,
  rows = 3
}: {
  value: string
  onChange: (v: string) => void
  members: Member[]
  placeholder?: string
  rows?: number
}) {
  const [mentionQuery, setMentionQuery] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const updateMentionState = (text: string, cursor: number) => {
    const before = text.slice(0, cursor)
    const match = before.match(/@([^\s@]*)$/)
    setMentionQuery(match ? match[1] : null)
  }

  const suggestions = useMemo(() => {
    if (mentionQuery === null) return []
    const q = mentionQuery.toLowerCase()
    return members
      .filter((m) => !q || memberLabel(m).toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q))
      .slice(0, 6)
  }, [mentionQuery, members])

  const insertMention = (m: Member) => {
    const el = textareaRef.current
    if (!el) return
    const cursor = el.selectionStart
    const before = value.slice(0, cursor).replace(/@[^\s@]*$/, `@${m.displayName || m.email} `)
    const after = value.slice(cursor)
    onChange(before + after)
    setMentionQuery(null)
    requestAnimationFrame(() => {
      el.focus()
      el.selectionStart = el.selectionEnd = before.length
    })
  }

  return (
    <div style={{ position: 'relative' }}>
      <textarea
        ref={textareaRef}
        className="form-input w-full"
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value)
          updateMentionState(e.target.value, e.target.selectionStart)
        }}
        onKeyUp={(e) => updateMentionState(e.currentTarget.value, e.currentTarget.selectionStart)}
        onBlur={() => setTimeout(() => setMentionQuery(null), 150)}
      />
      {mentionQuery !== null && suggestions.length > 0 && (
        <div className="dropdown-menu open" style={{ position: 'absolute', left: 0, top: '100%', zIndex: 60, minWidth: 240 }}>
          {suggestions.map((m) => (
            <button
              key={m.uid}
              type="button"
              className="dropdown-item w-full text-left flex items-center gap-2"
              onMouseDown={(e) => {
                e.preventDefault()
                insertMention(m)
              }}
            >
              <MemberAvatar member={m} size={20} />
              <span className="text-sm">{memberLabel(m)}</span>
              {m.email && <span className="text-xs text-secondary truncate">{m.email}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function TasksPage() {
  const { user, firebaseUser } = useAuth()
  const [searchParams] = useSearchParams()
  const focusTaskId = searchParams.get('task')

  const [tasks, setTasks] = useState<Task[]>([])
  const [members, setMembers] = useState<Member[]>([])
  const [watched, setWatched] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showDone, setShowDone] = useState(false)
  // ページ内タブ: 'all'（すべて）または表示メンバーのuid
  const [activeTab, setActiveTab] = useState<string>('all')

  // クイック追加
  const [quickTitle, setQuickTitle] = useState('')
  const [detailOpen, setDetailOpen] = useState(false)
  const [newDescription, setNewDescription] = useState('')
  const [newAssignee, setNewAssignee] = useState('')
  const [newDueDate, setNewDueDate] = useState('')
  const [creating, setCreating] = useState(false)

  // メンバーピッカー
  const [pickerOpen, setPickerOpen] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)

  // 編集
  const [editingId, setEditingId] = useState<string | null>(focusTaskId)
  const [editForm, setEditForm] = useState<{ title: string; description: string; assigneeUid: string; dueDate: string }>({
    title: '',
    description: '',
    assigneeUid: '',
    dueDate: ''
  })
  const [savingId, setSavingId] = useState<string | null>(null)

  const authFetch = useCallback(async (path: string, init?: RequestInit) => {
    if (!firebaseUser) throw new Error('未認証です')
    const token = await firebaseUser.getIdToken()
    return fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(init?.headers || {})
      }
    })
  }, [firebaseUser])

  const fetchAll = useCallback(async () => {
    if (!firebaseUser || !user?.orgId) return
    setLoading(true)
    setError(null)
    try {
      const [tRes, mRes, pRes] = await Promise.all([
        authFetch('/v1/tasks'),
        authFetch(`/v1/organizations/${user.orgId}/members`),
        authFetch('/v1/tasks/preferences')
      ])
      if (!tRes.ok) throw new Error(`タスクの取得に失敗しました (${tRes.status})`)
      const tData = await tRes.json()
      setTasks(tData.tasks || [])
      if (mRes.ok) {
        const mData = await mRes.json()
        setMembers(mData.members || [])
      }
      if (pRes.ok) {
        const pData = await pRes.json()
        setWatched(pData.watchedTaskMembers || [])
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'データの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }, [firebaseUser, user?.orgId, authFetch])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  // ピッカー外クリックで閉じる
  useEffect(() => {
    if (!pickerOpen) return
    const onClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [pickerOpen])

  const memberByUid = useMemo(() => {
    const map: Record<string, Member> = {}
    for (const m of members) map[m.uid] = m
    return map
  }, [members])

  const selfUid = user?.uid || ''
  // 表示メンバー: 自分 + ウォッチ中メンバー
  const visibleUids = useMemo(() => {
    const uids = [selfUid, ...watched.filter((w) => w !== selfUid && memberByUid[w])]
    return uids.filter(Boolean)
  }, [selfUid, watched, memberByUid])

  const visibleTasks = useMemo(() => {
    const set = new Set(visibleUids)
    let list = tasks.filter((t) => (t.assigneeUid ? set.has(t.assigneeUid) : t.createdBy === selfUid))
      .filter((t) => showDone || t.status !== 'done')
    // タブでメンバー絞り込み
    if (activeTab !== 'all') {
      list = list.filter((t) => t.assigneeUid === activeTab || (!t.assigneeUid && activeTab === selfUid && t.createdBy === selfUid))
    }
    return list
  }, [tasks, visibleUids, selfUid, showDone, activeTab])

  // 「すべて」タブで表示メンバーが複数のときは担当者ごとにグループ化
  const grouped = useMemo(() => {
    if (activeTab !== 'all' || visibleUids.length <= 1) return null
    return visibleUids.map((uid) => ({
      uid,
      member: memberByUid[uid],
      tasks: visibleTasks.filter((t) => t.assigneeUid === uid || (!t.assigneeUid && uid === selfUid && t.createdBy === selfUid))
    }))
  }, [activeTab, visibleUids, visibleTasks, memberByUid, selfUid])

  // タブが指すメンバーが表示メンバーから外れたら「すべて」に戻す
  useEffect(() => {
    if (activeTab !== 'all' && !visibleUids.includes(activeTab)) setActiveTab('all')
  }, [activeTab, visibleUids])

  const saveWatched = async (next: string[]) => {
    const prev = watched
    setWatched(next)
    try {
      const res = await authFetch('/v1/tasks/preferences', {
        method: 'PATCH',
        body: JSON.stringify({ watchedTaskMembers: next })
      })
      if (!res.ok) throw new Error(`${res.status}`)
    } catch {
      // 保存失敗時は表示を巻き戻してユーザーに知らせる
      setWatched(prev)
      setError('表示メンバー設定の保存に失敗しました')
    }
  }

  const createTask = async () => {
    const title = quickTitle.trim()
    if (!title || creating) return
    setCreating(true)
    setError(null)
    try {
      const res = await authFetch('/v1/tasks', {
        method: 'POST',
        body: JSON.stringify({
          title,
          ...(detailOpen && newDescription.trim() ? { description: newDescription.trim() } : {}),
          ...(detailOpen && newAssignee ? { assigneeUid: newAssignee } : { assigneeUid: selfUid }),
          ...(detailOpen && newDueDate ? { dueDate: newDueDate } : {})
        })
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || `タスクの作成に失敗しました (${res.status})`)
      }
      const data = await res.json()
      setTasks((prev) => [data.task, ...prev])
      setQuickTitle('')
      setNewDescription('')
      setNewAssignee('')
      setNewDueDate('')
      setDetailOpen(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'タスクの作成に失敗しました')
    } finally {
      setCreating(false)
    }
  }

  const patchTask = async (id: string, patch: Record<string, unknown>) => {
    setSavingId(id)
    try {
      const res = await authFetch(`/v1/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(patch) })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || `更新に失敗しました (${res.status})`)
      }
      const data = await res.json()
      setTasks((prev) => prev.map((t) => (t.id === id ? data.task : t)))
      return true
    } catch (e) {
      setError(e instanceof Error ? e.message : 'タスクの更新に失敗しました')
      return false
    } finally {
      setSavingId(null)
    }
  }

  // ── カラムソート（日付 / タイトル） ──
  const [sortField, setSortField] = useState<'dueDate' | 'title' | null>(null)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const toggleSort = (field: 'dueDate' | 'title') => {
    if (sortField === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }
  const sortTasks = (ts: Task[]): Task[] => {
    if (!sortField) return ts
    const dir = sortOrder === 'asc' ? 1 : -1
    return [...ts].sort((a, b) => {
      if (sortField === 'dueDate') {
        // 期限なしは常に末尾
        if (!a.dueDate && !b.dueDate) return 0
        if (!a.dueDate) return 1
        if (!b.dueDate) return -1
        return a.dueDate.localeCompare(b.dueDate) * dir
      }
      return a.title.localeCompare(b.title, 'ja') * dir
    })
  }
  const sortIndicator = (field: 'dueDate' | 'title') =>
    sortField === field ? (sortOrder === 'asc' ? ' ↑' : ' ↓') : ''

  const toggleDone = (task: Task) => {
    patchTask(task.id, { status: task.status === 'done' ? 'open' : 'done' })
  }

  const deleteTask = async (id: string) => {
    if (!window.confirm('このタスクを削除しますか？')) return
    try {
      const res = await authFetch(`/v1/tasks/${id}`, { method: 'DELETE' })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || `削除に失敗しました (${res.status})`)
      }
      setTasks((prev) => prev.filter((t) => t.id !== id))
      setEditingId(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'タスクの削除に失敗しました')
    }
  }

  const startEdit = (task: Task) => {
    setEditingId(task.id)
    setEditForm({
      title: task.title,
      description: task.description || '',
      assigneeUid: task.assigneeUid || '',
      dueDate: task.dueDate || ''
    })
  }

  // URLの ?task= で指定されたタスクの編集フォームを開く
  useEffect(() => {
    if (!focusTaskId || tasks.length === 0) return
    const task = tasks.find((t) => t.id === focusTaskId)
    if (task && editingId === focusTaskId && !editForm.title) startEdit(task)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTaskId, tasks])

  const saveEdit = async (id: string) => {
    const ok = await patchTask(id, {
      title: editForm.title.trim(),
      description: editForm.description.trim(),
      assigneeUid: editForm.assigneeUid || null,
      dueDate: editForm.dueDate || null
    })
    if (ok) setEditingId(null)
  }

  // カラム幅（ヘッダー行と行で共有）
  const COL = { check: 16, date: 96, link: 32, member: 28, trash: 24 }

  /** カラム名ヘッダー（日付・タイトルはクリックでソート） */
  const renderColumnHeader = () => (
    <div
      className="flex items-center gap-3 text-xs text-secondary"
      style={{ padding: '8px 12px', borderBottom: '1px solid var(--color-border-light)', background: 'var(--color-bg-subtle, #f9fafb)' }}
    >
      <span style={{ width: COL.check, flexShrink: 0 }} />
      <button
        type="button"
        className="text-xs text-secondary"
        onClick={() => toggleSort('dueDate')}
        style={{ width: COL.date, flexShrink: 0, textAlign: 'left', background: 'transparent', border: 0, cursor: 'pointer', padding: 0 }}
      >
        日付{sortIndicator('dueDate')}
      </button>
      <button
        type="button"
        className="text-xs text-secondary flex-1"
        onClick={() => toggleSort('title')}
        style={{ textAlign: 'left', background: 'transparent', border: 0, cursor: 'pointer', padding: 0, minWidth: 0 }}
      >
        タイトル{sortIndicator('title')}
      </button>
      <span style={{ width: COL.link, flexShrink: 0, textAlign: 'center' }}>リンク</span>
      <span style={{ width: COL.member, flexShrink: 0, textAlign: 'center' }}>担当</span>
      <span style={{ width: COL.trash, flexShrink: 0 }} />
    </div>
  )

  const renderTaskRow = (task: Task) => {
    const assignee = task.assigneeUid ? memberByUid[task.assigneeUid] : null
    const overdue = isOverdue(task.dueDate, task.status)
    const isEditing = editingId === task.id

    return (
      <div key={task.id} style={{ borderBottom: '1px solid var(--color-border-light)' }}>
        <div
          className="flex items-center gap-3"
          style={{ padding: '10px 12px', cursor: 'pointer' }}
          onClick={() => (isEditing ? setEditingId(null) : startEdit(task))}
        >
          <input
            type="checkbox"
            checked={task.status === 'done'}
            disabled={savingId === task.id}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleDone(task)}
            style={{ width: COL.check, height: 16, cursor: 'pointer', flexShrink: 0 }}
          />
          <span
            className="text-xs"
            style={{
              width: COL.date,
              flexShrink: 0,
              color: !task.dueDate ? 'var(--color-text-tertiary, #9ca3af)' : overdue ? '#dc2626' : 'var(--color-text-secondary, #6b7280)',
              fontWeight: overdue ? 600 : 400
            }}
          >
            {task.dueDate ? `${overdue ? '期限切れ ' : ''}${formatDue(task.dueDate)}` : '—'}
          </span>
          <span
            className="text-sm flex-1 truncate"
            title={task.title}
            style={{ minWidth: 0, ...(task.status === 'done' ? { textDecoration: 'line-through', color: 'var(--color-text-secondary, #6b7280)' } : {}) }}
          >
            {task.title}
          </span>
          <span style={{ width: COL.link, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
            {task.sourceDocumentId && (
              <Link
                to={`/documents/${task.sourceDocumentId}`}
                title={task.sourceDocumentTitle || '出典議事録を開く'}
                onClick={(e) => e.stopPropagation()}
                className="text-secondary"
                style={{ display: 'inline-flex' }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM14 2v6h6" />
                </svg>
              </Link>
            )}
          </span>
          <span style={{ width: COL.member, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
            <MemberAvatar member={assignee} size={22} />
          </span>
          <button
            className="btn-icon borderless"
            title="タスクを削除"
            onClick={(e) => { e.stopPropagation(); deleteTask(task.id) }}
            style={{ width: COL.trash, height: 24, padding: 0, flexShrink: 0, color: 'var(--color-text-tertiary, #9ca3af)' }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
              <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6" />
            </svg>
          </button>
        </div>

        {isEditing && (
          <div style={{ padding: '4px 12px 14px 40px', display: 'flex', flexDirection: 'column', gap: 10 }} onClick={(e) => e.stopPropagation()}>
            <input
              className="form-input w-full"
              value={editForm.title}
              onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="タイトル"
            />
            <MentionTextarea
              value={editForm.description}
              onChange={(v) => setEditForm((f) => ({ ...f, description: v }))}
              members={members}
              placeholder="説明（@でメンバーをメンション）"
            />
            <div className="flex items-center gap-3 flex-wrap">
              <label className="text-xs text-secondary flex items-center gap-2" style={{ whiteSpace: 'nowrap' }}>
                担当者
                <select
                  className="form-input"
                  style={{ minWidth: 0 }}
                  value={editForm.assigneeUid}
                  onChange={(e) => setEditForm((f) => ({ ...f, assigneeUid: e.target.value }))}
                >
                  <option value="">未割り当て</option>
                  {members.map((m) => (
                    <option key={m.uid} value={m.uid}>{memberLabel(m)}</option>
                  ))}
                </select>
              </label>
              <label className="text-xs text-secondary flex items-center gap-2" style={{ whiteSpace: 'nowrap' }}>
                期限
                <input
                  type="date"
                  className="form-input"
                  style={{ minWidth: 0 }}
                  value={editForm.dueDate}
                  onChange={(e) => setEditForm((f) => ({ ...f, dueDate: e.target.value }))}
                />
              </label>
            </div>
            <div className="flex items-center gap-2">
              <button className="btn btn-primary btn-sm" disabled={savingId === task.id || !editForm.title.trim()} onClick={() => saveEdit(task.id)}>
                保存
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditingId(null)}>キャンセル</button>
              <div className="flex-1" />
              <button className="btn btn-ghost btn-sm" style={{ color: '#dc2626' }} onClick={() => deleteTask(task.id)}>
                削除
              </button>
            </div>
            {(task.createdByName || task.sourceDocumentTitle) && (
              <div className="text-xs text-secondary">
                {task.createdByName && <>作成者: {task.createdByName}</>}
                {task.sourceDocumentTitle && (
                  <> ・ 出典: <Link to={`/documents/${task.sourceDocumentId}`}>{task.sourceDocumentTitle}</Link></>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  const selfMember = memberByUid[selfUid] || (user ? { uid: selfUid, email: user.email || '', displayName: user.displayName || '' } : null)
  const addableMembers = members.filter((m) => m.uid !== selfUid && !watched.includes(m.uid))

  return (
    <Layout title="TODO">
      <div style={{ maxWidth: 860, margin: '0 auto', width: '100%', minWidth: 0 }}>
        {/* 表示メンバーの管理（チップはタブと重複するため廃止し、ドロップダウンに統合） */}
        <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 16 }}>
          <div ref={pickerRef} style={{ position: 'relative' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setPickerOpen(!pickerOpen)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 13, height: 13 }}>
                <path d="M12 5v14M5 12h14" />
              </svg>
              メンバーを追加
            </button>
            {pickerOpen && (
              <div className="dropdown-menu open" style={{ position: 'absolute', left: 0, top: '100%', marginTop: 4, zIndex: 50, minWidth: 240, maxHeight: 280, overflowY: 'auto' }}>
                {watched.filter((w) => memberByUid[w]).map((uid) => (
                  <button
                    key={uid}
                    className="dropdown-item w-full text-left flex items-center gap-2"
                    title="クリックで表示から外す"
                    onClick={() => saveWatched(watched.filter((w) => w !== uid))}
                  >
                    <MemberAvatar member={memberByUid[uid]} size={20} />
                    <span className="text-sm flex-1">{memberLabel(memberByUid[uid])}</span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="var(--color-accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </button>
                ))}
                {addableMembers.length === 0 && watched.length === 0 && (
                  <div className="text-xs text-secondary" style={{ padding: '10px 12px' }}>追加できるメンバーがいません</div>
                )}
                {addableMembers.map((m) => (
                  <button
                    key={m.uid}
                    className="dropdown-item w-full text-left flex items-center gap-2"
                    onClick={() => {
                      saveWatched([...watched, m.uid])
                      setPickerOpen(false)
                    }}
                  >
                    <MemberAvatar member={m} size={20} />
                    <span className="text-sm">{memberLabel(m)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex-1" />
          <label className="text-xs text-secondary flex items-center gap-1" style={{ cursor: 'pointer' }}>
            <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} />
            完了済みも表示
          </label>
        </div>

        {/* タブ切り替え: すべて / 自分 / 各表示メンバー */}
        {visibleUids.length > 1 && (
          <div className="tab-list" style={{ marginBottom: 16 }}>
            <button
              className={`tab-item${activeTab === 'all' ? ' active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              すべて
            </button>
            {visibleUids.map((uid) => {
              const m = memberByUid[uid] || (uid === selfUid ? selfMember : null)
              if (!m) return null
              return (
                <button
                  key={uid}
                  className={`tab-item${activeTab === uid ? ' active' : ''}`}
                  onClick={() => setActiveTab(uid)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <MemberAvatar member={m} size={16} />
                  {uid === selfUid ? '自分' : memberLabel(m)}
                </button>
              )
            })}
          </div>
        )}

        {/* 新しいタスク */}
        <div className="card" style={{ marginBottom: 16, padding: 12 }}>
          <div className="flex items-center gap-2">
            <input
              className="form-input flex-1"
              placeholder="新しいタスクを入力（Enterで追加）"
              value={quickTitle}
              onChange={(e) => setQuickTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.nativeEvent.isComposing) createTask()
              }}
            />
            <button className="btn btn-ghost btn-sm" onClick={() => setDetailOpen(!detailOpen)} title="詳細を設定">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, transform: detailOpen ? 'rotate(180deg)' : undefined }}>
                <path d="M6 9l6 6 6-6" />
              </svg>
              詳細
            </button>
            <button className="btn btn-primary btn-sm" disabled={creating || !quickTitle.trim()} onClick={createTask}>
              {creating ? <span className="spinner sm" /> : '追加'}
            </button>
          </div>
          {detailOpen && (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <MentionTextarea
                value={newDescription}
                onChange={setNewDescription}
                members={members}
                placeholder="説明（@でメンバーをメンション）"
              />
              <div className="flex items-center gap-3 flex-wrap">
                <label className="text-xs text-secondary flex items-center gap-2" style={{ whiteSpace: 'nowrap' }}>
                  担当者
                  <select className="form-input" style={{ minWidth: 0 }} value={newAssignee} onChange={(e) => setNewAssignee(e.target.value)}>
                    <option value="">自分</option>
                    {members.map((m) => (
                      <option key={m.uid} value={m.uid}>{memberLabel(m)}</option>
                    ))}
                  </select>
                </label>
                <label className="text-xs text-secondary flex items-center gap-2" style={{ whiteSpace: 'nowrap' }}>
                  期限
                  <input type="date" className="form-input" style={{ minWidth: 0 }} value={newDueDate} onChange={(e) => setNewDueDate(e.target.value)} />
                </label>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="alert danger" style={{ marginBottom: 16 }}>
            <div className="alert-body"><p className="text-sm">{error}</p></div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center" style={{ padding: 48 }}>
            <span className="spinner" />
          </div>
        ) : grouped ? (
          // 複数メンバー表示: 担当者ごとにグループ化
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {grouped.map((g) => (
              <div key={g.uid} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="flex items-center gap-2" style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border-light)', background: 'var(--color-bg-subtle, #f9fafb)' }}>
                  <MemberAvatar member={g.member} size={22} />
                  <span className="text-sm font-medium">{memberLabel(g.member)}{g.uid === selfUid ? '（自分）' : ''}</span>
                  <span className="text-xs text-secondary">{g.tasks.filter((t) => t.status === 'open').length}件</span>
                </div>
                {g.tasks.length === 0 ? (
                  <div className="text-xs text-secondary" style={{ padding: '12px' }}>タスクはありません</div>
                ) : (
                  <>
                    {renderColumnHeader()}
                    {sortTasks(g.tasks).map(renderTaskRow)}
                  </>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {visibleTasks.length === 0 ? (
              <div className="empty-state" style={{ padding: 32 }}>
                <p className="text-sm text-secondary">タスクはありません。上の入力欄から追加できます。</p>
              </div>
            ) : (
              <>
                {renderColumnHeader()}
                {sortTasks(visibleTasks).map(renderTaskRow)}
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  )
}
